// src/features/admin/components/settings/integrations/components/EmailStorageTab.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ServerIcon,
  CloudIcon,
  CircleStackIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  Cog6ToothIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { getStorageIcon } from '@/components/icons/techIconMap';
import { EdgeMailAdminAPI } from '@/features/edgemail-admin/services/EdgeMailAdminAPI';
import type { EmailStorageConfig, EmailStorageSettings, StorageProviderType } from '@/features/edgemail-admin/types/admin.types';

interface ProviderDefinition {
  id: StorageProviderType;
  name: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  costPerGB: string;
  recommended?: boolean;
  features: string[];
}

const PROVIDERS: ProviderDefinition[] = [
  {
    id: 'local',
    name: 'Local Server (Dovecot mdbox)',
    description: 'Store emails directly on your server disk using Dovecot mdbox. Best for development, small firms, or cost-conscious deployments.',
    icon: ServerIcon,
    costPerGB: 'Free (disk space only)',
    recommended: true,
    features: [
      'No cloud dependency',
      'Full data control',
      'Fast local access',
      'Simple backup/restore',
      'No egress fees',
    ],
  },
  {
    id: 'gcs',
    name: 'Google Cloud Storage',
    description: 'Cloud-native object storage with automatic scaling, encryption, versioning, and compliance features.',
    icon: getStorageIcon('gcs') ?? CloudIcon,
    costPerGB: '$0.020-$0.023/GB/month',
    features: [
      'Auto-scaling',
      'Built-in redundancy',
      'Lifecycle policies',
      'Customer-managed encryption',
      '99.99% SLA',
    ],
  },
  {
    id: 's3',
    name: 'Amazon S3',
    description: 'AWS object storage for firms with existing AWS infrastructure. Supports S3-compatible storage providers.',
    icon: getStorageIcon('s3') ?? CloudIcon,
    costPerGB: '$0.023/GB/month',
    features: [
      'AWS ecosystem integration',
      'Glacier archival',
      'Cross-region replication',
      'Fine-grained IAM',
      'S3-compatible APIs',
    ],
  },
  {
    id: 'azure',
    name: 'Azure Blob Storage',
    description: 'Microsoft cloud storage for Microsoft 365 environments. Integrates with Azure Active Directory.',
    icon: getStorageIcon('azure') ?? CloudIcon,
    costPerGB: '$0.018/GB/month',
    features: [
      'Microsoft 365 integration',
      'Azure AD authentication',
      'Tiered storage options',
      'Immutable storage',
      'Azure compliance certifications',
    ],
  },
  {
    id: 'nas',
    name: 'On-Premise NAS',
    description: 'Connect to your existing network-attached storage for complete data sovereignty. Supports NFS, SMB, and iSCSI protocols.',
    icon: CircleStackIcon,
    costPerGB: 'Hardware cost only',
    features: [
      'Full data sovereignty',
      'No internet dependency',
      'Existing infrastructure',
      'Air-gapped option',
      'Custom retention',
    ],
  },
];

const SUPPORTED_TYPES = new Set<StorageProviderType>(['local', 'gcs', 'nas']);

const DEFAULT_SETTINGS: EmailStorageSettings = {
  mode: 'unified',
  body: {
    type: 'local',
    basePath: '/var/mail/edgemail',
  },
  attachments: {
    type: 'local',
    basePath: '/var/lib/edgemail-attachments',
  },
};

const normalizeSettings = (raw?: Partial<EmailStorageSettings>): EmailStorageSettings => {
  const mode = raw?.mode === 'split' || (raw as any)?.split_enabled ? 'split' : 'unified';
  const body = { ...DEFAULT_SETTINGS.body, ...(raw?.body || {}) } as EmailStorageConfig;
  const attachments = mode === 'split'
    ? ({ ...DEFAULT_SETTINGS.attachments, ...(raw?.attachments || {}) } as EmailStorageConfig)
    : body;

  return {
    mode,
    body,
    attachments,
  };
};

const EmailStorageTab: React.FC = () => {
  const [settings, setSettings] = useState<EmailStorageSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const attachmentConfigBackup = useRef<EmailStorageConfig | null>(null);

  useEffect(() => {
    const loadConfig = async () => {
      setLoading(true);
      setError(null);
      try {
        const config = await EdgeMailAdminAPI.getTenantConfig();
        const resolved = config.storage ?? (config.tenant?.settings?.email_storage as EmailStorageSettings | undefined);
        setSettings(normalizeSettings(resolved));
      } catch (err) {
        setError('Failed to load email storage configuration.');
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, []);

  const isSplit = settings.mode === 'split';

  const activeBodyProvider = useMemo(
    () => PROVIDERS.find((p) => p.id === settings.body.type) || PROVIDERS[0],
    [settings.body.type],
  );

  const activeAttachmentProvider = useMemo(
    () => PROVIDERS.find((p) => p.id === settings.attachments.type) || PROVIDERS[0],
    [settings.attachments.type],
  );

  const handleToggleSplit = (enabled: boolean) => {
    setSettings((prev) => {
      if (enabled) {
        return {
          ...prev,
          mode: 'split',
          attachments: attachmentConfigBackup.current || prev.attachments || prev.body,
        };
      }

      attachmentConfigBackup.current = prev.attachments;
      return {
        ...prev,
        mode: 'unified',
        attachments: prev.body,
      };
    });
  };

  const updateBodyConfig = (updates: Partial<EmailStorageConfig>) => {
    setSettings((prev) => {
      const body = { ...prev.body, ...updates } as EmailStorageConfig;
      return {
        ...prev,
        body,
        attachments: prev.mode === 'unified' ? body : prev.attachments,
      };
    });
  };

  const updateAttachmentConfig = (updates: Partial<EmailStorageConfig>) => {
    setSettings((prev) => ({
      ...prev,
      attachments: { ...prev.attachments, ...updates } as EmailStorageConfig,
    }));
  };

  const handleActivateBody = (type: StorageProviderType) => {
    updateBodyConfig({ type });
  };

  const handleActivateAttachments = (type: StorageProviderType) => {
    updateAttachmentConfig({ type });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await EdgeMailAdminAPI.updateTenantConfig({
        email_storage: settings,
      });
      setSuccessMessage('Email storage configuration saved.');
    } catch (err) {
      setError('Failed to save email storage configuration.');
    } finally {
      setSaving(false);
    }
  };

  const renderProviderGrid = (
    label: string,
    activeType: StorageProviderType,
    onActivate: (type: StorageProviderType) => void,
  ) => (
    <div className="space-y-4">
      <h4 className="text-md font-semibold text-gray-900 dark:text-white">{label}</h4>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {PROVIDERS.map((provider) => {
          const Icon = provider.icon;
          const isActive = provider.id === activeType;
          const isSupported = SUPPORTED_TYPES.has(provider.id);

          return (
            <div
              key={provider.id}
              className={`relative bg-white dark:bg-gray-800 rounded-lg border p-6 transition-all ${
                isActive
                  ? 'border-lex-accent ring-2 ring-lex-accent/20 shadow-lg'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              {provider.recommended && (
                <div className="absolute -top-2 -right-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300 border border-green-200 dark:border-green-800">
                    Recommended
                  </span>
                </div>
              )}

              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <div className={`p-3 rounded-lg ${
                    isActive
                      ? 'bg-lex-accent-soft'
                      : 'bg-gray-100 dark:bg-gray-700'
                  }`}>
                    <Icon className={`h-6 w-6 ${
                      isActive
                        ? 'text-lex-accent'
                        : 'text-gray-500 dark:text-gray-400'
                    }`} />
                  </div>
                  <div className="ml-4">
                    <h5 className="text-sm font-semibold text-gray-900 dark:text-white">
                      {provider.name}
                    </h5>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {isActive ? 'Active' : isSupported ? 'Available' : 'Coming soon'}
                    </span>
                  </div>
                </div>

                {isActive && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-lex-accent-soft text-lex-accent">
                    Active
                  </span>
                )}
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {provider.description}
              </p>

              <div className="mb-4">
                <h6 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                  Features
                </h6>
                <div className="flex flex-wrap gap-1">
                  {provider.features.map((feature) => (
                    <span
                      key={feature}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                <div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">Cost:</span>
                  <span className="ml-1 text-sm font-medium text-gray-900 dark:text-white">
                    {provider.costPerGB}
                  </span>
                </div>

                {!isActive && (
                  <button
                    onClick={() => onActivate(provider.id)}
                    disabled={!isSupported}
                    className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                      isSupported
                        ? 'text-lex-accent-contrast bg-lex-accent hover:bg-lex-accent-hover'
                        : 'text-gray-400 bg-gray-100 dark:bg-gray-700 cursor-not-allowed'
                    }`}
                  >
                    {isSupported ? 'Activate' : 'Coming soon'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderLocalConfig = (
    label: string,
    config: EmailStorageConfig,
    onChange: (updates: Partial<EmailStorageConfig>) => void,
  ) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-4">
      <h5 className="text-md font-semibold text-gray-900 dark:text-white flex items-center">
        <Cog6ToothIcon className="h-5 w-5 mr-2 text-lex-accent" />
        {label}
      </h5>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Base Path (tenant-scoped)
        </label>
        <input
          type="text"
          value={config.basePath || ''}
          onChange={(e) => onChange({ basePath: e.target.value })}
          className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-lex-accent focus:border-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          placeholder="/var/lib/edgemail-attachments"
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          EdgeMail automatically namespaces storage by tenant under this path.
        </p>
      </div>
    </div>
  );

  const renderGcsConfig = (
    label: string,
    config: EmailStorageConfig,
    onChange: (updates: Partial<EmailStorageConfig>) => void,
  ) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-4">
      <h5 className="text-md font-semibold text-gray-900 dark:text-white flex items-center">
        <Cog6ToothIcon className="h-5 w-5 mr-2 text-lex-accent" />
        {label}
      </h5>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            GCS Bucket Name
          </label>
          <input
            type="text"
            value={config.bucketName || ''}
            onChange={(e) => onChange({ bucketName: e.target.value })}
            className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-lex-accent focus:border-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            placeholder="edgelex-edgemail-bodies"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            GCP Project ID (optional)
          </label>
          <input
            type="text"
            value={config.projectId || ''}
            onChange={(e) => onChange({ projectId: e.target.value })}
            className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-lex-accent focus:border-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            placeholder="edgelex-prod"
          />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-500 dark:text-gray-400">
        <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
        Loading email storage configuration...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <ServerIcon className="h-6 w-6 text-lex-accent mr-2" />
            Email Storage Location
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Configure where EdgeMail stores message bodies and attachments. Defaults to unified storage unless split is enabled.
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 text-sm text-green-700 dark:text-green-300">
          {successMessage}
        </div>
      )}

      <div className="bg-lex-accent-soft border border-lex-accent/30 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <CheckCircleIcon className="h-5 w-5 text-lex-accent mr-2" />
            <span className="text-sm font-medium text-lex-accent">
              Active Body Storage: <strong>{activeBodyProvider.name}</strong>
              {isSplit && (
                <> | Attachment Storage: <strong>{activeAttachmentProvider.name}</strong></>
              )}
            </span>
          </div>
          <label className="flex items-center text-xs text-lex-accent">
            <input
              type="checkbox"
              checked={isSplit}
              onChange={(e) => handleToggleSplit(e.target.checked)}
              className="rounded border-lex-accent/40 text-lex-accent focus:ring-lex-accent mr-2"
            />
            Use separate storage for attachments
          </label>
        </div>
      </div>

      {renderProviderGrid('Mail Body Storage', settings.body.type, handleActivateBody)}

      {isSplit && renderProviderGrid('Attachment Storage', settings.attachments.type, handleActivateAttachments)}

      {settings.body.type === 'local' || settings.body.type === 'nas'
        ? renderLocalConfig('Mail Body Local Storage', settings.body, updateBodyConfig)
        : null}

      {settings.body.type === 'gcs'
        ? renderGcsConfig('Mail Body GCS Configuration', settings.body, updateBodyConfig)
        : null}

      {isSplit && (settings.attachments.type === 'local' || settings.attachments.type === 'nas')
        ? renderLocalConfig('Attachment Local Storage', settings.attachments, updateAttachmentConfig)
        : null}

      {isSplit && settings.attachments.type === 'gcs'
        ? renderGcsConfig('Attachment GCS Configuration', settings.attachments, updateAttachmentConfig)
        : null}

      <div className="flex items-center justify-between">
        <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex items-start">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400 mr-2 flex-shrink-0" />
          <div className="text-sm text-amber-700 dark:text-amber-300">
            <strong>Important:</strong> Changing storage providers on a production system requires data migration. Use the planned migration tooling before switching providers in production.
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="ml-4 inline-flex items-center px-4 py-2 text-sm font-medium text-lex-accent-contrast bg-lex-accent hover:bg-lex-accent-hover rounded-md transition-colors disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>

      <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
          Storage Migration
        </h5>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Storage migrations should be planned carefully. Attachments and bodies must remain consistent with the tenant configuration. The migration tooling will be wired once cloud storage providers are configured.
        </p>
      </div>
    </div>
  );
};

export default EmailStorageTab;
